import type { components } from '@/api/generated/schema'
import type { RestoreWizardConfig, WizardStage } from './types'

type RestoreInput = components['schemas']['RestoreInput']

/**
 * One `RestoreInput` builder for every machine — §18.1: "every restore job, of every kind, takes
 * exactly these inputs and nothing else." The `plan`/`order` stages already carry everything this
 * needs; this is just the one place that assembles the wire shape so the `plan` pane and the
 * `order` pane (which calls `restoreCreatePlan` a second time, on entry, per the spec's own "fired
 * without a control" table) never risk building two different objects for the same selection.
 */
export function buildRestoreInput(config: RestoreWizardConfig, stage: Extract<WizardStage, { stage: 'plan' } | { stage: 'order' }>, planDigest?: string): RestoreInput {
  return {
    restore_point_job_id: stage.restorePoint.data_snapshot.job_id,
    restore_kind: config.kind,
    target_scope: {
      repository_ids: stage.repositoryIds ? [...stage.repositoryIds] : stage.repositoryId ? [stage.repositoryId] : undefined,
      project_keys: stage.projectKeys ? [...stage.projectKeys] : undefined,
      workspace_level: config.targetScopeForm === 'WORKSPACE' ? true : undefined,
    },
    object_types: stage.objectTypes ? ([...stage.objectTypes] as unknown as RestoreInput['object_types']) : undefined,
    ref: stage.ref,
    destination_workspace_id: stage.destinationWorkspaceId,
    // REQUIRED "when the destination workspace differs from the source and the object set
    // includes anything naming a principal" (§18.7) — carried from `v-remap`'s own review, the
    // one screen that resolves it (W155).
    principal_map: stage.principalMap,
    // `acknowledgement` is REQUIRED on `RestoreInput` even for `restoreCreatePlan` — the same
    // schema serves all three calls (`plan`/`confirm`/`trigger`) and the plan is what MINTS the
    // digest, so there is nothing meaningful to send yet; an empty digest is sent for that call
    // only, and the real one flows in here for the confirmation and the trigger.
    acknowledgement: { plan_digest: planDigest ?? '' },
  }
}
